import { StarIcon } from "@phosphor-icons/react";
import { useSearch } from "@tanstack/react-router";
import { useEffect } from "react";
import { DropdownWithSubLabelButton } from "@/components/ui/button";
import {
  Menu,
  MenuItem,
  MenuPopover,
  MenuSeparator,
  MenuTrigger,
} from "@/components/ui/menu";
import type { SavedViewItem } from "@/data-access/saved-views/queries/saved-views-queries";
import { cn } from "@/lib/utils";
import useNewsFeedsStore from "@/store/news-feeds/news-feeds.slice";
import {
  getSavedViews,
  toggleSavedViewUserStar,
} from "@/store/saved-views-v2/saved-views-management.async-actions";
import useSavedViewStore from "@/store/saved-views-v2/saved-views-management.slice";
import { useAllSavedViewsArray } from "@/store/saved-views-v2/saved-views-management-selector";
import SavedViewFavoriteButton from "./SavedViewFavoriteButton";

export type DefaultView = {
  id: null;
  name: string;
};

const DEFAULT_VIEW: DefaultView = {
  id: null,
  name: "Newsfeed (Default View)",
};

export interface SavedViewsListProps {
  /** Callback when a view is selected - parent handles filter application */
  onViewSelect: (view: SavedViewItem | DefaultView) => void;
}

export const SavedViewsList = ({ onViewSelect }: SavedViewsListProps) => {
  // Get feedId from news feeds store (synced by TanStack routes)
  const currentFeedId = useNewsFeedsStore((state) => state.currentFeedId);

  // Saved views data from store
  const savedViews = useAllSavedViewsArray();
  const selectedSavedViewId = useSavedViewStore(
    (state) => state.selectedSavedViewId,
  );
  const setSelectedSavedViewId = useSavedViewStore(
    (state) => state.setSelectedSavedViewId,
  );
  const savedViewIdFromUrl = useSearch({
    strict: false,
    select: (search) =>
      "savedViewId" in search && typeof search.savedViewId === "string"
        ? search.savedViewId
        : undefined,
  });
  useEffect(() => {
    if (currentFeedId) {
      getSavedViews(currentFeedId);
    }
  }, [currentFeedId]);

  useEffect(() => {
    setSelectedSavedViewId(savedViewIdFromUrl ?? null);
  }, [savedViewIdFromUrl, setSelectedSavedViewId]);

  // Find the currently selected view
  const selectedView = savedViews.find(
    (view) => view.id === selectedSavedViewId,
  );
  const currentView: SavedViewItem | DefaultView = selectedView || DEFAULT_VIEW;

  const handleViewSelect = (view: SavedViewItem | DefaultView) => {
    setSelectedSavedViewId(view.id);
    onViewSelect(view);
  };

  const isSelectedViewStarred = selectedView?.isUserStarred ?? false;

  const handleToggleFavorite = () => {
    if (!selectedSavedViewId) return;

    void toggleSavedViewUserStar(selectedSavedViewId, !isSelectedViewStarred);
  };

  const starredSavedViews = savedViews.filter((view) => view.isUserStarred);
  const unstarredSavedViews = savedViews.filter((view) => !view.isUserStarred);

  const showFavoritesSection = starredSavedViews.length > 0;

  const renderSavedViewMenuItem = (
    view: SavedViewItem,
    options: { showStar: boolean },
  ) => {
    const isViewing = selectedSavedViewId === view.id;

    return (
      <MenuItem
        key={view.id}
        textValue={view.name}
        className={cn(
          "cursor-pointer text-xs text-gray-700 pr-4",
          isViewing && "font-medium",
        )}
        onAction={() => handleViewSelect(view)}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {options.showStar && (
            <StarIcon
              className="h-3 w-3 shrink-0 text-[#FFC005]"
              weight="fill"
              aria-hidden
            />
          )}
          <span className="overflow-hidden text-ellipsis whitespace-nowrap">
            {view.name}
          </span>
        </span>
        {isViewing && (
          <span className="ml-auto shrink-0 text-[10px] text-blue-600">
            Viewing
          </span>
        )}
      </MenuItem>
    );
  };

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <MenuTrigger>
        <DropdownWithSubLabelButton
          ariaLabel="saved-views-selector-button"
          label={currentView.name}
          className="flex-initial min-w-[min(16rem,100%)] max-w-[534px] p-3 items-center h-9 py-2 border bg-white text-violet-800"
          buttonSize="lg"
          buttonTitle={"Saved Views"}
          icon={
            isSelectedViewStarred ? (
              <StarIcon
                className="h-3 w-3 shrink-0 text-[#FFC005]"
                weight="fill"
                aria-hidden
              />
            ) : undefined
          }
        />
        <MenuPopover placement="bottom start">
          {savedViews.length === 0 ? (
            <Menu className="bg-white flex-initial my-2 mx-1 min-w-[534px] w-[534px]">
              <MenuItem
                isDisabled
                className="cursor-default text-xs text-gray-500"
              >
                No saved views
              </MenuItem>
            </Menu>
          ) : (
            <>
              {showFavoritesSection && (
                <div className="px-3 pt-2.5 pb-0 text-[10px] text-gray-500 select-none font-medium">
                  Favorites
                </div>
              )}
              <Menu className="bg-white flex-initial my-1 mx-1 min-w-[534px] w-[534px] max-h-[min(60vh,400px)] overflow-y-auto">
                {showFavoritesSection &&
                  starredSavedViews.map((view) =>
                    renderSavedViewMenuItem(view, { showStar: true }),
                  )}
                {showFavoritesSection && <MenuSeparator className="my-3" />}
                <MenuItem
                  className={cn(
                    "cursor-pointer text-xs text-gray-700 pr-4",
                    selectedSavedViewId === null && "font-medium",
                  )}
                  textValue={DEFAULT_VIEW.name}
                  onAction={() => handleViewSelect(DEFAULT_VIEW)}
                >
                  <span>Newsfeed (Default View)</span>
                  {selectedSavedViewId === null && (
                    <span className="ml-auto shrink-0 text-[10px] text-blue-600">
                      Viewing
                    </span>
                  )}
                </MenuItem>
                {unstarredSavedViews.map((view) =>
                  renderSavedViewMenuItem(view, { showStar: false }),
                )}
              </Menu>
            </>
          )}
        </MenuPopover>
      </MenuTrigger>
      {selectedSavedViewId !== null && (
        <SavedViewFavoriteButton
          isStarred={isSelectedViewStarred}
          onToggle={handleToggleFavorite}
        />
      )}
    </div>
  );
};
